import { motion } from "framer-motion";
import { Reveal, SectionTag, ArrowLink, EASE, MaskedLines } from "@/components/Reveal";

const STAGES = [
  {
    num: "01",
    label: "Identify",
    copy: "Understand the customer problem, market structure and opportunity for a better service experience.",
  },
  {
    num: "02",
    label: "Build",
    copy: "Create the technology, processes, customer journey and operational foundation required to launch.",
  },
  {
    num: "03",
    label: "Operate",
    copy: "Manage customer acquisition, service delivery, support, payments and day-to-day operational workflows.",
  },
  {
    num: "04",
    label: "Scale",
    copy: "Use technology, data and operational learning to improve efficiency, expand reach and strengthen the model.",
  },
];

export default function Approach() {
  return (
    <section
      id="approach"
      data-testid="approach-section"
      className="relative scroll-mt-20 overflow-hidden bg-[#070604] py-20 sm:py-24"
    >
      <div
        className="pointer-events-none absolute left-1/2 top-0 h-[40vh] w-[70vw] -translate-x-1/2 rounded-full bg-[#B8962E]/[0.07] blur-[130px]"
        aria-hidden="true"
      />
      <div className="grain-overlay pointer-events-none absolute inset-0" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <SectionTag index="03">Our Approach</SectionTag>
        </Reveal>
        <h2
          data-testid="approach-headline"
          className="mt-6 font-serif text-3xl font-normal leading-[1.1] tracking-tight text-slate-100 sm:text-4xl lg:text-5xl"
        >
          <MaskedLines delay={0.1}>
            Identify. Build.{" "}
            <span className="italic text-transparent bg-gradient-to-r from-[#F0DDA6] via-[#E7CE8B] to-[#C9A227] bg-clip-text">
              Operate. Scale.
            </span>
          </MaskedLines>
        </h2>

        {/* connected progression: desktop horizontal / mobile vertical */}
        <div data-testid="approach-progression" className="relative mt-16">
          <motion.span
            className="absolute left-0 right-0 top-[3px] hidden h-px origin-left bg-gradient-to-r from-[#E7CE8B] via-[#D4AF37]/50 to-white/10 lg:block"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 2.2, ease: EASE, delay: 0.2 }}
            aria-hidden="true"
          />
          <div className="grid gap-10 lg:grid-cols-4 lg:gap-10">
            {STAGES.map((s, i) => {
              const last = i === STAGES.length - 1;
              return (
                <Reveal key={s.num} delay={0.15 + i * 0.18}>
                  <div
                    data-testid={`pillar-card-${s.label.toLowerCase()}`}
                    className="group relative border-l border-[#D4AF37]/25 pl-6 lg:border-l-0 lg:pl-0"
                  >
                    <span
                      className={`absolute -left-[5px] top-0 h-[9px] w-[9px] rotate-45 lg:hidden ${last ? "bg-[#E7CE8B]" : "border border-[#D4AF37]/60 bg-[#070604]"}`}
                      aria-hidden="true"
                    />
                    <span className="relative mb-7 hidden lg:block" aria-hidden="true">
                      <motion.span
                        className={`block h-[9px] w-[9px] rotate-45 ${last ? "bg-[#E7CE8B]" : "border border-[#D4AF37]/60 bg-[#070604] transition-colors duration-500 group-hover:bg-[#D4AF37]"}`}
                        initial={{ scale: 0 }}
                        whileInView={{ scale: 1 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.5, ease: EASE, delay: 0.5 + i * 0.3 }}
                      />
                    </span>
                    <span className="block font-serif text-5xl font-normal leading-none tracking-tight text-white/[0.06] transition-colors duration-700 group-hover:text-[#D4AF37]/20" aria-hidden="true">
                      {s.num}
                    </span>
                    <p
                      className={`mt-5 font-serif text-xl font-normal uppercase tracking-tight transition-colors duration-500 sm:text-2xl ${
                        last ? "text-[#E7CE8B]" : "text-slate-100 group-hover:text-white"
                      }`}
                    >
                      {s.label}
                    </p>
                    <p className="mt-4 max-w-xs text-[15px] font-normal leading-relaxed text-stone-400 transition-colors duration-500 group-hover:text-stone-300">
                      {s.copy}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>

        <Reveal delay={0.2}>
          <ArrowLink to="/approach" testid="approach-explore-link">
            Explore Our Approach
          </ArrowLink>
        </Reveal>
      </div>
    </section>
  );
}
